import { Description, Tune } from '@mui/icons-material';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import {
  createLegalCollections,
  getAvailableDocuments,
} from 'lib/api/legalCollections';
import { AvailableDocument } from 'lib/api/legalCollections/types';
import { DateInput, TextInput } from 'lib/common/Inputs';
import CustomButton from 'lib/designSystem/Button';
import TableCard from 'lib/designSystem/Card/TableCard';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import SmallHeaderWithIcon from 'lib/designSystem/SmallHeaderWithIcon';
import {
  formatDate,
  formatMoney,
  showAlert,
  validaRut,
} from 'lib/functions/utils';
import { SummaryRow } from 'pages/Finance/Components/Financing/common/Summary';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

interface CreateLegalCollectionProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const CreateLegalCollection: React.FC<CreateLegalCollectionProps> = ({
  open,
  setOpen,
}) => {
  const dispatch = useDispatch();
  const [selectedDocument, setSelectedDocument] =
    useState<AvailableDocument | null>(null);
  const newStartDate = new Date(new Date().setUTCHours(12, 0, 0, 0));
  const [startDate, setStartDate] = useState<Date>(newStartDate);
  const [lawyerIdentifier, setLawyerIdentifier] = useState<string>('');
  const [lawyerName, setLawyerName] = useState<string>('');
  const { data, isLoading } = getAvailableDocuments(dispatch);
  const availableDocuments = data?.documents || [];

  const {
    mutate: createLegalCollectionsMutation,
    isPending: isCreatingLegalCollection,
  } = createLegalCollections(dispatch);

  const disabledCreate =
    !selectedDocument || isLoading || isCreatingLegalCollection;

  const validParams = () => {
    if (!selectedDocument?.id) {
      showAlert(dispatch, 'error', 'Debes seleccionar un documento');
      return false;
    }
    if (!startDate) {
      showAlert(
        dispatch,
        'error',
        'Debes seleccionar una fecha de inicio de proceso',
      );
      return false;
    }
    if (lawyerIdentifier && !validaRut(lawyerIdentifier)) {
      showAlert(dispatch, 'error', 'Rut abogado/firma inválido');
      return false;
    }
    return true;
  };

  const handleCreate = async () => {
    if (!validParams()) return;
    createLegalCollectionsMutation(
      {
        legalCollections: [
          {
            documentId: selectedDocument!.id,
            startDate,
            lawyerIdentifier:
              lawyerIdentifier === '' ? undefined : lawyerIdentifier,
            lawyerName: lawyerName === '' ? undefined : lawyerName,
          },
        ],
      },
      {
        onSuccess: () => {
          showAlert(
            dispatch,
            'success',
            'Proceso de cobranza judicial creado exitosamente',
          );
          setOpen(false);
        },
        onError: () => {
          showAlert(
            dispatch,
            'error',
            'Ha habido un error al crear el proceso de cobranza judicial',
          );
        },
      },
    );
  };

  return (
    <div>
      <Dialog open={open} maxWidth="lg" fullWidth>
        <DialogTitle>Crear proceso de cobranza judicial</DialogTitle>
        <DialogContent>
          {isLoading || isCreatingLegalCollection ? (
            <div style={{ minHeight: 100 }}>
              <CenteredCircularProgress type="dialog" />
            </div>
          ) : (
            <Box>
              <TableCard
                size="small"
                enabledEffects={false}
                sx={{
                  boxShadow: 'none',
                  background: 'transparent',
                  border: 'none',
                }}
              >
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ marginBottom: 16 }}
                >
                  Selecciona el documento que quieres enviar a cobranza
                  judicial.
                </Typography>
                <Autocomplete
                  options={availableDocuments}
                  value={selectedDocument}
                  onChange={(event, newValue) => setSelectedDocument(newValue)}
                  getOptionLabel={(option: AvailableDocument) =>
                    `${option.folio} | ${option.businessName} | ${option.stakeholderName}`
                  }
                  renderInput={(params: AutocompleteRenderInputParams) => (
                    <TextField
                      {...params}
                      label="Busca documentos por folio, emisor o deudor"
                      placeholder="Selecciona un documento..."
                    />
                  )}
                  filterSelectedOptions
                  fullWidth
                  sx={{ marginBottom: 2 }}
                />
                {selectedDocument && (
                  <>
                    <SmallHeaderWithIcon
                      icon={<Description fontSize="small" />}
                      title="Datos del documento"
                    />
                    <Grid container spacing={0} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <Box sx={{ pr: 2 }}>
                          <SummaryRow
                            label="Tipo documento"
                            value={selectedDocument.typeEsp}
                            labelVariant="body2"
                            valueVariant="body2"
                          />
                          <SummaryRow
                            label="Folio"
                            value={selectedDocument.folio}
                            labelVariant="body2"
                            valueVariant="body2"
                          />
                          <SummaryRow
                            label="Monto documento"
                            value={formatMoney(selectedDocument.amount)}
                            labelVariant="body2"
                            valueVariant="body2"
                          />
                          <SummaryRow
                            label="Saldo documento"
                            value={formatMoney(
                              selectedDocument.documentRemainingAmount,
                            )}
                            labelVariant="body2"
                            valueVariant="body2"
                            showBorder={false}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            pl: 2,
                            borderLeft: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <SummaryRow
                            label="Emisor"
                            value={selectedDocument.businessName}
                            labelVariant="body2"
                            valueVariant="body2"
                            valueMaxLength={40}
                          />
                          <SummaryRow
                            label="Rut emisor"
                            value={selectedDocument.businessIdentifier}
                            labelVariant="body2"
                            valueVariant="body2"
                          />
                          <SummaryRow
                            label="Deudor"
                            value={selectedDocument.stakeholderName}
                            labelVariant="body2"
                            valueVariant="body2"
                            valueMaxLength={40}
                          />
                          <SummaryRow
                            label="Rut deudor"
                            value={selectedDocument.stakeholderIdentifier}
                            labelVariant="body2"
                            valueVariant="body2"
                            showBorder={false}
                          />
                        </Box>
                      </Grid>
                    </Grid>

                    <SmallHeaderWithIcon
                      icon={<Tune fontSize="small" />}
                      title="Parámetros"
                    />
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 20,
                      }}
                    >
                      <DateInput
                        label="Fecha de inicio"
                        value={formatDate(startDate)}
                        disabled={isLoading}
                        onChange={(value) =>
                          setStartDate(
                            new Date(new Date(value).setUTCHours(12, 0, 0, 0)),
                          )
                        }
                      />
                      <TextInput
                        label="Rut abogado/firma (Opcional)"
                        value={lawyerIdentifier}
                        disabled={isLoading}
                        onChange={(value) => setLawyerIdentifier(value ?? '')}
                      />
                      <TextInput
                        label="Nombre abogado/firma (Opcional)"
                        value={lawyerName}
                        disabled={isLoading}
                        onChange={(value) => setLawyerName(value ?? '')}
                      />
                    </div>
                  </>
                )}
              </TableCard>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <CustomButton
            color="secondary"
            onClick={() => setOpen(false)}
            disabled={isCreatingLegalCollection}
          >
            Cancelar
          </CustomButton>
          <CustomButton
            color="primary"
            onClick={handleCreate}
            disabled={disabledCreate}
          >
            Crear
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CreateLegalCollection;
